import React from 'react';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import { aiAdminTokens as t } from './tokens';

// El servicio contesta 200 siempre: un proveedor sin saldo consultable lo dice
// en su propio `status`, así que "unsupported" o "missing_credentials" no son
// fallas del panel y se muestran como información, no como error.
const STATUS_STYLES = {
    ok: { bg: t.color.okSoft, fg: t.color.ok },
    unsupported: { bg: t.color.chipBg, fg: t.color.fgMuted },
    missing_credentials: { bg: t.color.warnSoft, fg: t.color.warn },
    error: { bg: '#FDECE7', fg: t.color.danger },
};

function ProviderCard({ tr, provider, info }) {
    const known = Object.prototype.hasOwnProperty.call(STATUS_STYLES, info.status);
    const style = known ? STATUS_STYLES[info.status] : STATUS_STYLES.error;
    // Un status que el servicio agregue después no tiene traducción todavía: se
    // muestra crudo en vez de disparar el warning de `t`.
    const label = known ? tr(`ai_status_${info.status}`) : info.status;

    return (
        <Box sx={{
            bgcolor: t.color.surface,
            border: `1px solid ${t.color.border}`,
            borderRadius: `${t.radius.lg}px`,
            boxShadow: t.shadow.sm,
            p: 2.5,
            minWidth: 240,
            flex: '1 1 240px',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontFamily: t.font.sans, fontSize: 14, fontWeight: 600 }}>
                    {provider}
                </Typography>
                <Chip
                    label={label}
                    size="small"
                    sx={{ height: 22, fontSize: 11.5, fontFamily: t.font.sans, bgcolor: style.bg, color: style.fg }}
                />
            </Box>

            {info.status === 'ok' ? (
                <Typography sx={{ fontFamily: t.font.sans, fontSize: 26, fontWeight: 700, mt: 1.5 }}>
                    {`${info.balance ?? 0} ${info.currency ?? ''}`.trim()}
                </Typography>
            ) : (
                <Typography sx={{ fontFamily: t.font.sans, fontSize: 12.5, mt: 1.5 }}>
                    {info.detail || '—'}
                </Typography>
            )}
        </Box>
    );
}

export default function AccountTab({ tr, account }) {
    if (account.loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress/></Box>;
    }
    if (account.error) {
        return (
            <Alert severity="error">
                {typeof account.error === 'string' ? account.error : tr('ai_admin_service_down')}
            </Alert>
        );
    }

    const providers = Object.entries(account.data?.providers ?? {});

    return (
        <Box>
            <Typography sx={{ fontFamily: t.font.sans, fontSize: 12.5, mb: 1.5 }}>
                {tr('ai_admin_account_note')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {providers.map(([provider, info]) => (
                    <ProviderCard key={provider} tr={tr} provider={provider} info={info}/>
                ))}
            </Box>
        </Box>
    );
}
